import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../App.css";
import { AiOutlineFileSearch } from "react-icons/ai";
import { BiEditAlt } from "react-icons/bi";
import { FaFilePdf } from "react-icons/fa";
import { RiRefreshFill } from "react-icons/ri";
import { FaCheckCircle, FaTimesCircle, FaClock } from "react-icons/fa";

const GestionProveedoresHome = () => {
  const [datos, setDatos] = useState([]);
  const [filtrados, setFiltrados] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [documentosFiltrados, setDocumentosFiltrados] = useState([]);
  const [estadoFiltro, setEstadoFiltro] = useState("Todos");
  const [idFiltro] = useState(""); // , setIdFiltro
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const navigate = useNavigate();

  // --- Función segura para parsear fechas en varios formatos ---
  const parseDateSafe = (value) => {
    if (!value) return null;

    // Si ya es Date
    if (value instanceof Date) {
      if (isNaN(value)) return null;
      return value;
    }

    const s = String(value).trim();

    // ISO yyyy-mm-dd (input type=date)
    const isoMatch = /^\d{4}-\d{2}-\d{2}$/;
    if (isoMatch.test(s)) {
      const d = new Date(s);
      return isNaN(d) ? null : d;
    }

    // DD/MM/YYYY or D/M/YYYY (formato local)
    const dmy = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
    const m = s.match(dmy);
    if (m) {
      const day = parseInt(m[1], 10);
      const month = parseInt(m[2], 10) - 1; // monthIndex
      const year = parseInt(m[3], 10);
      const d = new Date(year, month, day);
      return isNaN(d) ? null : d;
    }

    // Fallback: intentar Date constructor (menos fiable)
    const fallback = new Date(s);
    return isNaN(fallback) ? null : fallback;
  };

  // normaliza fecha a medianoche para comparar por día
  const normalizeToMidnight = (d) => {
    const nd = new Date(d);
    nd.setHours(0, 0, 0, 0);
    return nd;
  };

  useEffect(() => {
    const fetchData = async () => {
      const cuit = localStorage.getItem("cuit");

      try {
        const res = await fetch(`/api/getGestionProveedorByCUIT?cuit=${cuit}`);
        const data = await res.json();
        setDatos(data);
        setFiltrados(data);

        const docRes = await fetch(`/api/getDocumentacionByCUIT?cuit=${cuit}`);
        const docDataRaw = await docRes.json();

        // Procesar documentos para determinar estado vencido automaticamente
        const hoy = normalizeToMidnight(new Date());

        const docData = (Array.isArray(docDataRaw) ? docDataRaw : []).map((doc) => {
          let estadoDoc = doc["Estado de Documento"] || "";
          const vencStr = doc["Vencimiento Documento"];

          const venc = parseDateSafe(vencStr);
          if (venc) {
            const vencNormalized = normalizeToMidnight(venc);
            // Si vencimiento es hoy o anterior => Vencido
            if (vencNormalized <= hoy) {
              estadoDoc = "Vencido";
            }
          }

          // Retornamos doc enriquecido (puede incluir flags para UI)
          return {
            ...doc,
            "Estado de Documento": estadoDoc,
            __isExpired: estadoDoc === "Vencido",
          };
        });

        setDocumentos(docData);
        setDocumentosFiltrados(docData);
      } catch (error) {
        console.error("❌ Error cargando solicitudes/documentos:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let solicitudesFiltradas = [...datos];
    let docsFiltrados = [...documentos];

    if (estadoFiltro !== "Todos") {
      solicitudesFiltradas = solicitudesFiltradas.filter((item) => item.estado === estadoFiltro);
      docsFiltrados = docsFiltrados.filter((doc) => doc["Estado de Documento"] === estadoFiltro);
    }

    if (idFiltro) {
      solicitudesFiltradas = solicitudesFiltradas.filter((item) => item.idProveedor.includes(idFiltro));
      docsFiltrados = docsFiltrados.filter((doc) => doc["ID Proveedor"]?.includes(idFiltro));
    }

    if (fechaDesde && fechaHasta) {
      const desde = new Date(fechaDesde);
      const hasta = new Date(fechaHasta);

      solicitudesFiltradas = solicitudesFiltradas.filter((item) => {
        const fecha = new Date(item.fecha);
        return fecha >= desde && fecha <= hasta;
      });

      docsFiltrados = docsFiltrados.filter((doc) => {
        const fecha = new Date(doc["Fecha Subida"]);
        return fecha >= desde && fecha <= hasta;
      });
    }

    setFiltrados(solicitudesFiltradas);
    setDocumentosFiltrados(docsFiltrados);
  }, [estadoFiltro, idFiltro, fechaDesde, fechaHasta, datos, documentos]);

  const redirigirDocumentacion = (idRegistro) => {
    localStorage.setItem("idRegistro", idRegistro);
    navigate("/documentacion");
  };

  const redirigirReemplazo = (documento) => {
    localStorage.setItem("documentoRechazado", JSON.stringify(documento));
    navigate("/reemplazo-documento");
  };

  return (
    <div className="cuenta-corriente-container">
      <h2>Gestión de Proveedores</h2>

      {/* Filtros */}
      <div className="filtros">
        <label>
          Estado:
          <select value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
            <option value="Todos">Todos</option>
            <option value="Pendiente">Pendiente</option>
            <option value="Aprobado">Aprobado</option>
            <option value="Rechazado">Rechazado</option>
            <option value="Vencido">Vencido</option>
          </select>
        </label>

        <label>
          Desde:
          <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
        </label>

        <label>
          Hasta:
          <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
        </label>

        <div className="nuevo-btn-container">
          <button className="primary-button" onClick={() => navigate("/informacion-personal")}>
            Nueva Solicitud
          </button>
        </div>
      </div>

      {/* Tabla de Solicitudes */}
      <table className="details-table">
        <thead>
          <tr>
            <th>ID Registro</th>
            <th>Proveedor</th>
            <th>Fecha</th>
            <th>Estado</th>
            <th>Comentarios</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filtrados.length > 0 ? (
            filtrados.map((item, index) => (
              <tr key={index} className={item.estado === "Aprobado" ? "fila-aprobada" : item.estado === "Rechazado" ? "fila-rechazada" : ""}>
                <td>{item.idProveedor}</td>
                <td>{item.proveedor}</td>
                <td>{item.fecha}</td>
                <td>
                  {item.estado === "Aprobado" && (
                    <>
                      <FaCheckCircle style={{ color: "#2e7d32", marginRight: "5px" }} />
                      <span style={{ color: "#2e7d32" }}>{item.estado}</span>
                    </>
                  )}
                  {item.estado === "Rechazado" && (
                    <>
                      <FaTimesCircle style={{ color: "#c62828", marginRight: "5px" }} />
                      <span style={{ color: "#c62828" }}>{item.estado}</span>
                    </>
                  )}
                  {item.estado === "Pendiente" && (
                    <>
                      <FaClock style={{ color: "#757575", marginRight: "5px" }} />
                      <span style={{ color: "#757575" }}>{item.estado}</span>
                    </>
                  )}
                  {!["Aprobado", "Rechazado", "Pendiente"].includes(item.estado) && item.estado}
                </td>

                <td>{item.comentarios || "-"}</td>
                <td>
                {item.estado === "Aprobado" && item.tipoProveedor === "Servicios" && (
                  <button className="icon-button" onClick={() => redirigirDocumentacion(item.idProveedor)}>
                    <AiOutlineFileSearch size={20} />
                  </button>
                )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6">No hay resultados para mostrar.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Tabla de Documentación */}
      <h2 style={{ marginTop: "20px" }}>Documentación Presentada</h2>
      <table className="details-table">
        <thead>
          <tr>
            <th>Tipo de Documento</th>
            <th>Fecha Subida</th>
            <th>Archivo Adjunto</th>
            <th>Link Archivo</th>
            <th>Vencimiento</th>
            <th>Estado</th>
            <th>Observaciones</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
        {documentosFiltrados.length > 0 ? (
            documentosFiltrados.map((doc, index) => {
              const estado = doc["Estado de Documento"];
              const rowClass =
                estado === "Aprobado" ? "fila-aprobada" : estado === "Rechazado" ? "fila-rechazada" : estado === "Vencido" ? "fila-vencida" : estado === "Actualizado" ? "fila-actualizada" :"";

              return (
                <tr key={index} className={rowClass}>
                  <td>{doc["Tipo de Documento"]}</td>
                  <td>{doc["Fecha Subida"]}</td>
                  <td>{doc["Archivo Adjunto"]}</td>
                  <td>
                    <a href={doc["Link Archivo"]} target="_blank" rel="noopener noreferrer">
                      <FaFilePdf size={18} color="#d32f2f" />
                    </a>
                  </td>
                  <td>{doc["Vencimiento Documento"] || "N/A"}</td>
                  <td>
                    {estado === "Aprobado" && (
                      <>
                        <FaCheckCircle style={{ color: "#2e7d32", marginRight: "5px" }} />
                        <span style={{ color: "#2e7d32" }}>{estado}</span>
                      </>
                    )}
                    {estado === "Rechazado" && (
                      <>
                        <FaTimesCircle style={{ color: "#c62828", marginRight: "5px" }} />
                        <span style={{ color: "#c62828" }}>{estado}</span>
                      </>
                    )}
                    {estado === "Pendiente" && (
                      <>
                        <FaClock style={{ color: "#757575", marginRight: "5px" }} />
                        <span style={{ color: "#757575" }}>{estado}</span>
                      </>
                    )}
                    {estado === "Vencido" && (
                      <>
                        <FaTimesCircle style={{ color: "#e68900", marginRight: "5px" }} />
                        <span style={{ color: "#e68900" }}>{estado}</span>
                      </>
                    )}
                    {estado === "Actualizado" && (
                      <>
                        <RiRefreshFill style={{ color: "#2e7d32", marginRight: "5px" }} />
                        <span style={{ color: "#2e7d32" }}>{estado}</span>
                      </>
                    )}
                    {!["Aprobado", "Rechazado", "Pendiente", "Vencido", "Actualizado"].includes(estado) && estado}
                  </td>

                  <td>{doc["Observaciones"] || "-"}</td>
                  <td>
                    { (doc["Estado de Documento"] === "Rechazado" || doc["Estado de Documento"] === "Vencido") && (
                      <button className="icon-button" onClick={() => redirigirReemplazo(doc)}>
                        <BiEditAlt size={20} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="8">No hay documentos cargados.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default GestionProveedoresHome;

